import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useLearn } from "@/contexts/LearnContext";
import { Progress } from "@/components/ui/progress";
import QuizBlock from "@/components/QuizBlock";
import ModuleDiagram from "@/components/ModuleDiagram";
import StoryCard from "@/components/StoryCard";
import {
  Clock,
  ArrowLeft,
  ArrowRight,
  Bookmark,
  BookmarkCheck,
  Check,
  CheckCircle2,
} from "lucide-react";

const FALLBACK_IMAGES = {
  m1: "https://images.pexels.com/photos/34804021/pexels-photo-34804021.jpeg",
  m2: "https://images.pexels.com/photos/17483871/pexels-photo-17483871.png",
  m3: "https://images.pexels.com/photos/10325707/pexels-photo-10325707.png",
  m4: "https://images.unsplash.com/photo-1758746285882-f8ff1a727207",
  m5: "https://images.pexels.com/photos/17483871/pexels-photo-17483871.png",
  m6: "https://images.pexels.com/photos/34804021/pexels-photo-34804021.jpeg",
  m7: "https://images.pexels.com/photos/17483871/pexels-photo-17483871.png",
  m8: "https://images.unsplash.com/photo-1758746285882-f8ff1a727207",
  m9: "https://images.pexels.com/photos/10325707/pexels-photo-10325707.png",
  m10: "https://images.pexels.com/photos/34804021/pexels-photo-34804021.jpeg",
};

export default function ModulePage() {
  const { moduleId } = useParams();
  const {
    content,
    markProgress,
    isSubtopicDone,
    isBookmarked,
    toggleBookmark,
    getModuleProgress,
    overallProgress,
  } = useLearn();
  const navigate = useNavigate();

  const module = content?.modules.find((m) => m.id === moduleId);
  const prev = content?.modules[(module?.number ?? 1) - 2] || null;
  const next = content?.modules[(module?.number ?? 1)] || null;

  useEffect(() => {
    // scroll to anchored subtopic if hash present
    const hash = window.location.hash?.slice(1);
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [moduleId]);

  if (!content || !module) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center text-muted-foreground">
        Loading module…
      </div>
    );
  }

  const pct = getModuleProgress(module);

  return (
    <div className="page-ambient">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-12 pb-24">
        {/* Module header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-primary mb-6">
            <span className="chapter-num text-4xl text-primary">
              {String(module.number).padStart(2, "0")}
            </span>
            <span>Module {module.number} of 10</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {module.title}
          </h1>
          <p className="mt-5 text-muted-foreground text-lg max-w-3xl leading-relaxed">
            {module.summary}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-5 text-xs">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Clock size={13} /> {module.duration_hours} hrs total
            </span>
            <span className="font-mono text-muted-foreground">
              Module progress: <span className="text-primary">{pct}%</span>
            </span>
          </div>
          <Progress value={pct} className="h-1 mt-3" />

          <div className="mt-6 p-5 border-l-2 border-primary bg-accent/30 rounded-r">
            <div className="text-[11px] uppercase tracking-[0.25em] text-primary mb-1">
              Key take-away
            </div>
            <p className="text-sm">{module.takeaway}</p>
          </div>
        </div>

        {/* Real-world story + optional video */}
        {(module.story || module.story_video) && (
          <StoryCard
            story={module.story}
            youtubeId={module.story_video}
            moduleNumber={module.number}
          />
        )}

        {/* Hero diagram (AI generated) */}
        <div className="mb-14">
          <ModuleDiagram
            moduleId={module.id}
            title={module.title}
            fallback={FALLBACK_IMAGES[module.id]}
          />
        </div>

        {/* Subtopics */}
        <div className="space-y-14">
          {module.subtopics.map((s, idx) => {
            const done = isSubtopicDone(module.id, s.id);
            const marked = isBookmarked(module.id, s.id);
            return (
              <article
                id={s.id}
                key={s.id}
                data-testid={`subtopic-${s.id}`}
                className="scroll-mt-28"
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                      Sub-topic {module.number}.{idx + 1} · {s.reading_time_min} min read
                    </div>
                    <h2 className="font-serif text-2xl md:text-3xl leading-snug tracking-tight">
                      {s.title}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleBookmark(module.id, s.id, s.title)}
                    data-testid={`bookmark-btn-${s.id}`}
                    aria-label={marked ? "Remove bookmark" : "Bookmark this sub-topic"}
                    className={`shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-full border transition-colors ${
                      marked
                        ? "border-primary text-primary bg-accent"
                        : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                    }`}
                  >
                    {marked ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
                  </button>
                </div>

                <div className="prose-reading">
                  {s.sections.map((sec, i) => (
                    <div key={i} className="mb-6">
                      <h3 className="font-serif text-lg md:text-xl mb-2 text-primary">
                        {sec.heading}
                      </h3>
                      {sec.body.split("\n").map((line, j) =>
                        line.startsWith("  ") || line.match(/^\s*[A-Za-z_]+\s*=/) ? (
                          <pre key={j}>{line}</pre>
                        ) : (
                          <p key={j}>{line}</p>
                        )
                      )}
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => markProgress(module.id, s.id)}
                  disabled={done}
                  data-testid={`mark-done-${s.id}`}
                  className={`mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm rounded border transition-colors ${
                    done
                      ? "border-primary text-primary bg-accent cursor-default"
                      : "border-border hover:border-primary hover:text-primary"
                  }`}
                >
                  {done ? (
                    <>
                      <CheckCircle2 size={14} /> Completed
                    </>
                  ) : (
                    <>
                      <Check size={14} /> Mark as read
                    </>
                  )}
                </button>
              </article>
            );
          })}
        </div>

        {/* Quizzes */}
        <div className="mt-20">
          <div className="flex items-center gap-3 mb-6">
            <span className="chapter-num text-3xl text-primary">Q</span>
            <h2 className="font-serif text-2xl md:text-3xl">Check your understanding</h2>
          </div>
          <div className="space-y-6">
            {module.quizzes.map((q, i) => (
              <QuizBlock key={q.id} moduleId={module.id} quiz={q} index={i} />
            ))}
          </div>
        </div>

        {/* Footer nav */}
        <div className="mt-20 pt-8 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          {prev ? (
            <button
              type="button"
              onClick={() => navigate(`/module/${prev.id}`)}
              data-testid="prev-module-btn"
              className="group inline-flex items-center gap-3 text-left px-4 py-3 border border-border rounded hover:border-primary transition-colors"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Previous</div>
                <div className="text-sm font-medium">{prev.title}</div>
              </div>
            </button>
          ) : (
            <Link
              to="/home"
              className="group inline-flex items-center gap-3 px-4 py-3 border border-border rounded hover:border-primary"
            >
              <ArrowLeft size={16} /> Back to home
            </Link>
          )}

          {next ? (
            <button
              type="button"
              onClick={() => navigate(`/module/${next.id}`)}
              data-testid="next-module-btn"
              className="group inline-flex items-center justify-end gap-3 text-right px-4 py-3 border border-border rounded hover:border-primary transition-colors sm:text-right"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Next</div>
                <div className="text-sm font-medium">{next.title}</div>
              </div>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          ) : overallProgress() >= 100 ? (
            <Link
              to="/certificate"
              data-testid="go-to-certificate-btn"
              className="inline-flex items-center justify-center gap-3 px-5 py-3 bg-primary text-primary-foreground rounded"
            >
              View Certificate <ArrowRight size={16} />
            </Link>
          ) : (
            <Link
              to="/home"
              data-testid="finish-all-btn"
              className="inline-flex items-center justify-center gap-3 px-5 py-3 border border-border rounded hover:border-primary"
            >
              Finish remaining modules <ArrowRight size={16} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
